import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

import { selectAssignee } from 'flow/selectors/TicketsSelectors';
import { getStringWithFirstLetterFromSecondWord } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import { Contact } from 'flow/types/Contact';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';

import useAppContext from 'flow/hooks/useAppContext';
import {
    selectContact,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';

const getAssigneeDisplayText = (
    i18next: any,
    ownExtensionKey: ExtensionKey,
    assigneeKey: 'unassigned' | ExtensionKey,
    assigneeContact?: TypedMap<Contact>,
    abbreviateLastName?: boolean,
) => {
    if (assigneeKey === 'unassigned') {
        return i18next.t('tickets.unassigned.singular');
    } else if (assigneeKey === ownExtensionKey) {
        return i18next.t('chat.you');
    } else {
        const assigneeFullName = assigneeContact
            ? `${assigneeContact.get('firstName')} ${assigneeContact.get(
                  'lastName',
              )}`
            : '';
        return abbreviateLastName
            ? getStringWithFirstLetterFromSecondWord(assigneeFullName)
            : assigneeFullName;
    }
};

interface Props {
    ticketKey?: TicketKey;
    assigneeKey?: 'unassigned' | ExtensionKey;
    abbreviateLastName?: boolean;
    className?: string;
}
const TicketAssigneeItem = ({
    ticketKey,
    assigneeKey,
    abbreviateLastName,
    className,
}: Props) => {
    const { i18next } = useAppContext();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const assignee = useAppSelector(
        (state) => assigneeKey || selectAssignee(state, ticketKey),
    );
    const assigneeContact = useAppSelector((state) =>
        assignee === 'unassigned' ? undefined : selectContact(state, assignee),
    );

    const assigneeDisplayText = getAssigneeDisplayText(
        i18next,
        ownExtensionKey,
        assignee,
        assigneeContact,
        abbreviateLastName,
    );

    return (
        <span className={twMerge('line-clamp-1 break-all', className)}>
            {assigneeDisplayText}
        </span>
    );
};

export default React.memo(TicketAssigneeItem);
